import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

const emptyEvent = { title: "", venueName: "", venueArea: "", fullAddress: "", city: "Pune", eventDate: "", eventTime: "", reportingTime: "", ageBand: "", miniDatesPerGuest: 6, avgMatches: 3, dressCode: "Smart casual", totalMaleSpots: 12, totalFemaleSpots: 12, eventType: "speed-dating", eventStatus: "published", tags: [], heroCardVisible: false, theme: "", description: "" };
const FormField = ({ label, field, form, setForm, type = "text", half = false }) => (
  <div className={half ? "flex-1 min-w-[120px]" : ""}>
    <label className="text-xs font-medium text-muted-foreground mb-1 block">{label}</label>
    <Input 
      type={type} 
      value={form[field] || ""} 
      onChange={(e) => setForm({ ...form, [field]: type === "number" ? (parseInt(e.target.value) || 0) : e.target.value })} 
      className="rounded-sm text-sm h-8" 
    />
  </div>
);

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEvent);
  const [tagInput, setTagInput] = useState("");

  const fetchEvents = useCallback(() => {
    axios.get(`${API}/admin/events`, authHeaders()).then(r => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const openCreate = () => { setEditing(null); setForm(emptyEvent); setTagInput(""); setDialogOpen(true); };
  const openEdit = (e) => { setEditing(e.id); setForm({ ...emptyEvent, ...e }); setTagInput((e.tags || []).join(", ")); setDialogOpen(true); };

  const handleSave = async () => {
    const payload = { ...form, tags: tagInput.split(",").map(t => t.trim()).filter(Boolean) };
    try {
      if (editing) {
        await axios.put(`${API}/admin/events/${editing}`, payload, authHeaders());
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/admin/events`, payload, authHeaders());
        toast.success("Event created");
      }
      setDialogOpen(false); fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/admin/events/${id}`, authHeaders());
      toast.success("Event deleted"); fetchEvents();
    } catch { toast.error("Failed to delete"); }
  };

  const setHero = async (id) => {
    try {
      await axios.put(`${API}/admin/events/${id}/hero`, {}, authHeaders());
      toast.success("Hero event updated"); fetchEvents();
    } catch { toast.error("Failed"); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/events/${id}`, { eventStatus: status }, authHeaders());
      toast.success(`Status: ${status}`); fetchEvents();
    } catch { toast.error("Failed"); }
  };


  return (
    <div data-testid="admin-events">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl tracking-tight font-medium">Events</h1>
        <Button onClick={openCreate} className="rounded-sm gap-2" data-testid="admin-create-event-btn"><Plus className="w-4 h-4" /> Create Event</Button>
      </div>

      <div className="space-y-3">
        {events.map((e) => (
          <div key={e.id} className="bg-card border border-border/40 rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid={`admin-event-${e.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-sm truncate">{e.title}</span>
                {e.heroCardVisible && <Badge variant="secondary" className="text-[10px] rounded-full">HERO</Badge>}
                <Badge variant={e.eventStatus === "published" ? "default" : "secondary"} className="text-[10px] rounded-full">{e.eventStatus}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">{e.venueArea} / {e.eventDate} {e.eventTime} / Age {e.ageBand} / {e.bookingCount || 0} bookings, {e.waitlistCount || 0} waitlist</p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setHero(e.id)} title="Set as hero"><Star className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(e)}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(e.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
              {e.eventStatus === "published" && <Button variant="outline" size="sm" className="text-xs h-7 rounded-sm" onClick={() => updateStatus(e.id, "sold_out")}>Sold Out</Button>}
              {e.eventStatus === "draft" && <Button variant="outline" size="sm" className="text-xs h-7 rounded-sm" onClick={() => updateStatus(e.id, "published")}>Publish</Button>}
              {e.eventStatus === "published" && <Button variant="outline" size="sm" className="text-xs h-7 rounded-sm" onClick={() => updateStatus(e.id, "draft")}>Unpublish</Button>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-card">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? "Edit Event" : "Create Event"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <FormField label="Title" field="title" form={form} setForm={setForm} />
            <div className="flex gap-3">
              <FormField label="Venue Name" field="venueName" form={form} setForm={setForm} half />
              <FormField label="Venue Area" field="venueArea" form={form} setForm={setForm} half />
            </div>
            <FormField label="Full Address" field="fullAddress" form={form} setForm={setForm} />
            <div className="flex gap-3">
              <FormField label="Date (YYYY-MM-DD)" field="eventDate" form={form} setForm={setForm} half />
              <FormField label="Time" field="eventTime" form={form} setForm={setForm} half />
            </div>
            <div className="flex gap-3">
              <FormField label="Reporting Time" field="reportingTime" form={form} setForm={setForm} half />
              <FormField label="Age Band" field="ageBand" form={form} setForm={setForm} half />
            </div>
            <div className="flex gap-3">
              <FormField label="Male Spots" field="totalMaleSpots" form={form} setForm={setForm} type="number" half />
              <FormField label="Female Spots" field="totalFemaleSpots" form={form} setForm={setForm} type="number" half />
            </div>
            <div className="flex gap-3">
              <FormField label="Mini-Dates/Guest" field="miniDatesPerGuest" form={form} setForm={setForm} type="number" half />
              <FormField label="Avg Matches" field="avgMatches" form={form} setForm={setForm} type="number" half />
            </div>
            <FormField label="Dress Code" field="dressCode" form={form} setForm={setForm} />
            <FormField label="Theme" field="theme" form={form} setForm={setForm} />
            <FormField label="Description" field="description" form={form} setForm={setForm} />
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Tags (comma-separated)</label>
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} className="rounded-sm text-sm h-8" placeholder="Hosted speed-dating, Mini-dates + games" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Status</label>
              <select value={form.eventStatus} onChange={(e) => setForm({ ...form, eventStatus: e.target.value })} className="w-full h-8 rounded-sm border border-input bg-transparent px-2 text-sm">
                {["draft", "published", "sold_out", "completed", "cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <Button onClick={handleSave} className="w-full rounded-sm" data-testid="admin-save-event-btn">{editing ? "Update Event" : "Create Event"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
